import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, Lock, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

import { makeRequest } from "@/utils/makeRequest";
import { getErrorMessage } from "@/utils/getErrorMessage";

import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const ResetPasswordPage = () => {
  const { resetToken } = useParams();
  const navigate = useNavigate();

  const [passwordHash, setPasswordHash] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const passwordStrength = () => {
    let strength = 0;
    if (passwordHash.length >= 8) strength++;
    if (/[A-Z]/.test(passwordHash)) strength++;
    if (/[0-9]/.test(passwordHash)) strength++;
    if (/[^A-Za-z0-9]/.test(passwordHash)) strength++;
    return strength;
  };

  const getPasswordStrengthLabel = () => {
    const strength = passwordStrength();
    if (strength === 0) return "";
    if (strength === 1) return "Weak";
    if (strength === 2) return "Fair";
    if (strength === 3) return "Good";
    return "Strong";
  };

  const getPasswordStrengthColor = () => {
    const strength = passwordStrength();
    if (strength <= 1) return "bg-red-500";
    if (strength === 2) return "bg-yellow-500";
    if (strength === 3) return "bg-blue-500";
    return "bg-emerald-500";
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (passwordHash !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setLoading(true);

    try {
      const res = await makeRequest.put(`/auth/reset-password/${resetToken}`, {
        passwordHash,
      });

      if (res.data) {
        navigate("/login");
      }

      toast.success(res.data.message);
      setSubmitted(true);
    } catch (error) {
      toast.error(getErrorMessage(error));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-2">
          <CheckCircle2 className="mx-auto text-emerald-500 w-10 h-10" />
          <h2 className="text-xl font-semibold">Password reset successful</h2>
          <p className="text-muted-foreground">
            You can now log in with your new password.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-background px-4">
      <div className="w-full sm:max-w-md mx-auto">
        <div className="nex-card p-2 space-y-6">
          <h1 className="text-xl font-bold text-center">Reset Password</h1>

          <form onSubmit={handleSubmit} className="space-y-10">
            {/* Password */}
            <div className="space-y-2">
              <Label className="text-base">Password</Label>

              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" />

                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="New password"
                  value={passwordHash}
                  onChange={(e) => setPasswordHash(e.target.value)}
                  className="pl-10 pr-10 !h-12"
                  required
                />

                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 !h-12 px-3"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff /> : <Eye />}
                </Button>
              </div>

              {passwordHash && (
                <div className="space-y-1">
                  <div className="flex gap-1">
                    {[1, 2, 3, 4].map((i) => (
                      <div
                        key={i}
                        className={cn(
                          "h-1 flex-1 rounded",
                          i <= passwordStrength()
                            ? getPasswordStrengthColor()
                            : "bg-muted",
                        )}
                      />
                    ))}
                  </div>

                  <p className="text-sm">{getPasswordStrengthLabel()}</p>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <Label className="text-base">Confirm Password</Label>

              <Input
                type={showPassword ? "text" : "password"}
                placeholder="Confirm password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="!h-12"
              />

              {confirmPassword && passwordHash !== confirmPassword && (
                <p className="text-xs text-red-500">Passwords do not match</p>
              )}

              {confirmPassword && passwordHash === confirmPassword && (
                <p className="text-sm text-emerald-500 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" />
                  Passwords match
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-[#F0B90B] text-black font-semibold rounded-lg disabled:opacity-50"
            >
              {loading ? "Resetting..." : "Reset Password"}
              <ArrowRight className="w-5 h-5 inline ml-2" />
            </button>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-primary hover:underline w-full justify-center"
            >
              Back to Login
            </Link>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
